<template>
  <div
class="mt-2 h-fit text-sm text-gray-700 flex flex-col gap-2"
  
  :class="{ 'p-0': minimized, 'p-4': !minimized }"
  >
    <div v-if="statsStore.isLoading" class="flex items-center justify-center py-8">
      <LoaderCircle class="h-6 w-6 animate-spin text-gray-400" />
    </div>

    <template v-else>
      <div class="flex gap-4">
        <div class="flex items-center justify-between">
          <div
            class="text-md flex flex-col p-4 rounded-lg border-cosmicPurple/20 border-[1px] bg-gradient-to-tr from-cosmicPurple/10 to-white"
          >
            Total points:
            <div class="flex items-center gap-0.5">
              <div class="animate-sweep relative">
                <img src="/ot_coin.svg" class="h-7 w-7" />
              </div>
              <span class="text-2xl">{{ userStats?.total_points.toFixed(1) || '0.0' }}</span>
            </div>
          </div>
        </div>

        <div class="flex-1">
          <div class="flex justify-between w-full font-normal">
            Current week tasks:
            <span class="font-medium"
              >{{ userStats?.current_week_done_tasks_count || 0 }}/{{
                userStats?.current_week_tasks_count || 0
              }}
              done</span
            >
          </div>
          <div class="flex justify-between w-full font-normal">
            Total completed tasks:
            <span class="font-medium">{{ userStats?.done_tasks_count || 0 }}</span>
          </div>

          <div class="flex justify-between w-full font-normal">
            Required tasks completed:
            <span class="font-medium">{{ userStats?.on_track_percentage || 0 }}%</span>
          </div>

          <div class="flex justify-between w-full font-normal">
            Status:
            <ProgressStatusDisplay 
              :status="userStats?.status || ''" 
              :user-progress="userStats"
              :show-details="true"
            />
          </div>
          

            <router-link v-if="userStats?.strike_count > 0" to="/profile/" class="flex items-center gap-1 w-full -mt-1">
              <ExternalLink class="w-4 h-4" />
              <span>Warnings:</span>
              <span class="font-medium flex-grow text-right">{{ userStats?.strike_count }}</span>
            </router-link>
        </div>
      </div>

      <router-link 
        v-if="!isGuide"
        to="/my-progress"
        class="w-full text-sm"
      >
        <div class="border-[1px] border-gray-200 rounded-md p-2 text-xs text-center hover:bg-gray-50 hover:translate-y-[-1px] hover:border-cosmicPurple/10 hover:text-gray-700 transition-all duration-200 fadeIn">
          Show details
        </div>
      </router-link>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useStatsStore } from '@/stores/stats'
import { useExpeditionsStore } from '@/stores/expeditions'
import { LoaderCircle, ExternalLink } from 'lucide-vue-next'
import ProgressStatusDisplay from '@/components/shared/ProgressStatusDisplay.vue'
import { useAuth } from '@/composables/useAuth'

const props = defineProps<{
  userData?: any,
  minimized?: boolean
}>()

const statsStore = useStatsStore()
const expeditionsStore = useExpeditionsStore()
const { isGuide } = useAuth()
const userStats = computed(() => props.userData || statsStore.stats?.current_user)

const fetchStats = async () => {
  if (expeditionsStore.latestExpeditionId) {
    await statsStore.fetchAdventurerStats(String(expeditionsStore.latestExpeditionId))
  }
}

onMounted(() => {
  if (!props.userData) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchStats()
  }
})

// Also fetch when expedition ID changes (only if no userData prop)
watch(
  () => expeditionsStore.latestExpeditionId,
  (newId) => {
    if (newId && !props.userData) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchStats()
    }
  }
)

</script>

<style scoped>
.animate-sweep {
  position: relative;
  overflow: hidden;
}

.animate-sweep::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.3), transparent);
  transform: rotate(45deg);
  animation: sweep 1.5s infinite;
}

@keyframes sweep {
  0% {
    transform: translate(-100%, -100%) rotate(45deg);
  }
  50% {
    transform: translate(0%, 0%) rotate(45deg);
  }
  80% {
    transform: translate(100%, 100%) rotate(45deg);
  }
  100% {
    transform: translate(100%, 100%) rotate(45deg);
  }
}
</style>
